if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        // Perceived speed: open the cart drawer immediately so the user sees
        // instant response. The existing drawer contents show first; when the
        // server response arrives (~200-500ms later), renderContents() swaps
        // in the fresh HTML including the newly-added item.
        if (this.cart && this.cart.tagName === 'CART-DRAWER' && typeof this.cart.open === 'function') {
          try { this.cart.open(this.submitButton); } catch (e) {}
        }

        // Optimistic placeholder row: insert a "Loading..." row in the cart drawer
        // immediately so the user sees the item appear instantly. When the server
        // response arrives, renderContents() replaces the entire drawer body and
        // this placeholder is naturally removed.
        try { this._insertOptimisticRow(); } catch (e) { /* fall back silently */ }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      // Insert a placeholder row into the cart drawer immediately on click so
      // the user gets instant visual feedback while /cart/add.js is in flight.
      // The row is marked data-optimistic-add="true" so other cart logic
      // (cartDomHasSixPack, estimateCartTotalCents) can skip it. When the
      // server response lands, renderContents() replaces the drawer body and
      // this placeholder is gone.
      _insertOptimisticRow() {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;

        const title = (this.dataset.cardTitle || '').trim();
        const image = (this.dataset.cardImage || '').trim();
        if (!title) return; // No data — bail silently and let server response handle it.

        // Strip pack suffix + parenthetical for unselected display ("Plain Delicious 4pk (32 Tortillas)" → "Plain Delicious")
        let displayTitle = title.split('(')[0]
          .replace(/ ([246])-?(pack|pk)/gi, '')
          .trim();

        // Empty-state detection: the `is-empty` class lives on <cart-drawer>
        // itself, and on <cart-drawer-items>. Both are reliable signals.
        const isEmpty =
          drawer.classList.contains('is-empty') ||
          (drawer.querySelector('cart-drawer-items') &&
           drawer.querySelector('cart-drawer-items').classList.contains('is-empty'));

        const tbody = drawer.querySelector('cart-drawer-items tbody');

        // Build the placeholder row HTML
        const rowHtml =
          '<tr class="cart-item" role="row" data-optimistic-add="true" data-unselected="true" data-unit-price-cents="0">' +
            '<td class="cart-item__media" role="cell">' +
              (image ? '<img class="cart-item__image" src="' + this._escAttr(image) + '" alt="" loading="lazy" width="150" height="150">' : '') +
            '</td>' +
            '<td class="cart-item__details" role="cell">' +
              '<a class="cart-item__name h3 body-title break">' + this._escHtml(displayTitle) + '</a>' +
              '<p class="caption-with-letter-spacing light" style="margin-top:8px;opacity:0.6;">Adding to cart…</p>' +
            '</td>' +
            '<td class="cart-item__totals right" role="cell">' +
              '<div class="d-flex flex-direction-column position-relative w-100">' +
                '<div class="loading__spinner" style="display:inline-block;"><svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle></svg></div>' +
              '</div>' +
            '</td>' +
            '<td class="cart-item__quantity" role="cell"></td>' +
          '</tr>';

        if (tbody) {
          // Cart already has items — append at the end.
          tbody.insertAdjacentHTML('beforeend', rowHtml);
        } else if (isEmpty) {
          // Empty cart — drop the empty state and inject minimal table structure
          // so we can show the placeholder. The server response will overwrite
          // this with the full cart-drawer HTML.
          drawer.classList.remove('is-empty');
          const drawerInner = drawer.querySelector('.drawer__inner');
          if (drawerInner) drawerInner.classList.remove('is-empty');
          const cartDrawerItems = drawer.querySelector('cart-drawer-items');
          if (cartDrawerItems) cartDrawerItems.classList.remove('is-empty');

          // Hide all empty-state visuals
          const emptyContent = drawer.querySelector('.drawer__inner-empty');
          if (emptyContent) emptyContent.style.display = 'none';
          // Hide the "Your cart is empty" / continue-shopping text inside the wrapper
          const emptyText = drawer.querySelectorAll('.empty-cart-text, .cart__empty-text, .continue-shopify');
          emptyText.forEach((el) => { el.style.display = 'none'; });

          // Find the CartItems container and inject minimal table structure
          const itemsContainer = drawer.querySelector('#CartDrawer-CartItems');
          if (!itemsContainer) return;
          itemsContainer.innerHTML =
            '<div class="drawer__cart-items-wrapper">' +
              '<table class="cart-items" role="table"><tbody role="rowgroup">' + rowHtml + '</tbody></table>' +
            '</div>';
        }
      }

      _escHtml(s) {
        return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
      }
      _escAttr(s) {
        return String(s).replace(/"/g, '&quot;');
      }
    }
  );
}